/**
 * Estatísticas de Chamadas de Entrada
 * VigosCloud - Incoming Call System
 *
 * Padrão visual: CallcenterV2 (ccv2-*)
 */

import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  accessLocalStorage,
  getIncomingStatistics,
  getAllIncomingConfigs
} from '../../../services';

import Loading from '../../Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPhone,
  faTimesCircle,
  faServer,
  faRecordVinyl,
  faVolumeUp,
  faExchangeAlt,
  faSync
} from '@fortawesome/free-solid-svg-icons';

import './IncomingCalls.css';
import '../../../pages/CallcenterV2/config-styles.css';

function IncomingCallsStatistics() {
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [userTenant, setUserTenant] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const user = await accessLocalStorage.getUserLocalStorage();
      if (!user?.user?.idtenant) {
        history.push('/');
        return;
      }

      const tenant = user.user.idtenant;
      setUserTenant(tenant);

      const [statsResponse, configsResponse] = await Promise.all([
        getIncomingStatistics(tenant),
        getAllIncomingConfigs({ tenant, activeOnly: false })
      ]);

      if (statsResponse.success) {
        setStatistics(statsResponse.data);
      }

      if (configsResponse.success) {
        setConfigs(configsResponse.data || []);
      }

    } catch (error) {
      console.error('[INCOMING-STATS] Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadData();
  };

  const calculatePercentage = (value, total) => {
    if (total === 0) return 0;
    return ((value / total) * 100).toFixed(1);
  };

  const getDestinationTypeLabel = (type) => {
    const types = {
      endpoint: 'Ramal',
      queue: 'Fila',
      'callcenter-v2': 'Callcenter V2',
      external: 'Externo',
      disa: 'DISA/URA',
      whatsapp: 'WhatsApp',
      hangup: 'Desligar',
      stasis: 'App Stasis'
    };
    return types[type] || type;
  };

  if (loading) {
    return <Loading />;
  }

  if (!statistics) {
    return (
      <div className="incoming-page">
        <div className="incoming-content">
          <div className="ccv2-config-card" style={{ padding: '2rem', textAlign: 'center', color: '#dc2626' }}>
            Não foi possível carregar as estatísticas
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="incoming-page">
      <div className="incoming-content">

        {/* Header */}
        <div className="incoming-header">
          <div className="incoming-title-wrapper">
            <FontAwesomeIcon icon={faPhone} className="incoming-icon" />
            <div>
              <h1 className="incoming-title">Estatísticas - Chamadas de Entrada</h1>
              <p className="incoming-subtitle">Acompanhe o panorama geral e distribuições</p>
            </div>
          </div>
          <div className="incoming-actions">
            <button className="ccv2-btn ccv2-btn-sm" style={{ background: '#0ea5e9', color: '#fff' }} onClick={handleRefresh}>
              <FontAwesomeIcon icon={faSync} /> Atualizar
            </button>
            <Link to="/config/chamadas-entrada" className="ccv2-btn ccv2-btn-sm ccv2-btn-outline" style={{ textDecoration: 'none', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>
              <FontAwesomeIcon icon={faArrowLeft} /> Voltar
            </Link>
          </div>
        </div>

        {/* Resumo Geral - KPIs */}
        <div className="ccv2-dashboard-kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="ccv2-dashboard-kpi" style={{ borderLeft: '4px solid #4A7BA7' }}>
            <div className="ccv2-dashboard-kpi-value" style={{ color: '#4A7BA7' }}>
              {statistics.total_configs || 0}
            </div>
            <div className="ccv2-dashboard-kpi-label">Total de Configurações</div>
          </div>
          <div className="ccv2-dashboard-kpi" style={{ borderLeft: '4px solid #22c55e' }}>
            <div className="ccv2-dashboard-kpi-value" style={{ color: '#22c55e' }}>
              {statistics.active_configs || 0}
            </div>
            <div className="ccv2-dashboard-kpi-label">
              Ativas ({calculatePercentage(statistics.active_configs, statistics.total_configs)}%)
            </div>
          </div>
          <div className="ccv2-dashboard-kpi" style={{ borderLeft: '4px solid #dc2626' }}>
            <div className="ccv2-dashboard-kpi-value" style={{ color: '#dc2626' }}>
              {statistics.inactive_configs || 0}
            </div>
            <div className="ccv2-dashboard-kpi-label">
              Inativas ({calculatePercentage(statistics.inactive_configs, statistics.total_configs)}%)
            </div>
          </div>
        </div>

        {/* Distribuição por Tipo de Destino */}
        <div className="ccv2-config-card" style={{ padding: '1.5rem' }}>
          <h3 className="incoming-section-title">
            <FontAwesomeIcon icon={faPhone} /> Distribuição por Tipo de Destino
          </h3>
          <div className="incoming-resources-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))' }}>
            <div className="incoming-resource-card info">
              <FontAwesomeIcon icon={faPhone} className="incoming-resource-icon" />
              <div className="incoming-resource-text">
                <div className="incoming-resource-title">Ramais</div>
                <div className="incoming-resource-value" style={{ fontSize: '1.25rem' }}>
                  {statistics.endpoint_destinations || 0}
                </div>
                <div style={{ fontSize: '0.6875rem', opacity: 0.7 }}>
                  {calculatePercentage(statistics.endpoint_destinations, statistics.total_configs)}% do total
                </div>
              </div>
            </div>

            <div className="incoming-resource-card warning">
              <FontAwesomeIcon icon={faExchangeAlt} className="incoming-resource-icon" />
              <div className="incoming-resource-text">
                <div className="incoming-resource-title">Filas</div>
                <div className="incoming-resource-value" style={{ fontSize: '1.25rem' }}>
                  {statistics.queue_destinations || 0}
                </div>
                <div style={{ fontSize: '0.6875rem', opacity: 0.7 }}>
                  {calculatePercentage(statistics.queue_destinations, statistics.total_configs)}% do total
                </div>
              </div>
            </div>

            <div className="incoming-resource-card" style={{ background: '#e0e7ff', color: '#3730a3' }}>
              <FontAwesomeIcon icon={faPhone} className="incoming-resource-icon" />
              <div className="incoming-resource-text">
                <div className="incoming-resource-title">Externos</div>
                <div className="incoming-resource-value" style={{ fontSize: '1.25rem' }}>
                  {statistics.external_destinations || 0}
                </div>
                <div style={{ fontSize: '0.6875rem', opacity: 0.7 }}>
                  {calculatePercentage(statistics.external_destinations, statistics.total_configs)}% do total
                </div>
              </div>
            </div>

            <div className="incoming-resource-card enabled">
              <FontAwesomeIcon icon={faPhone} className="incoming-resource-icon" />
              <div className="incoming-resource-text">
                <div className="incoming-resource-title">DISA/URA</div>
                <div className="incoming-resource-value" style={{ fontSize: '1.25rem' }}>
                  {statistics.disa_destinations || 0}
                </div>
              </div>
            </div>

            <div className="incoming-resource-card" style={{ background: '#fee2e2', color: '#991b1b' }}>
              <FontAwesomeIcon icon={faTimesCircle} className="incoming-resource-icon" />
              <div className="incoming-resource-text">
                <div className="incoming-resource-title">Desligar</div>
                <div className="incoming-resource-value" style={{ fontSize: '1.25rem' }}>
                  {statistics.hangup_destinations || 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recursos Habilitados */}
        <div className="ccv2-config-card" style={{ padding: '1.5rem' }}>
          <h3 className="incoming-section-title">Recursos Habilitados</h3>
          <div className="incoming-resources-grid">
            <div className="incoming-resource-card info">
              <FontAwesomeIcon icon={faServer} className="incoming-resource-icon" />
              <div className="incoming-resource-text">
                <div className="incoming-resource-title">Com Tronco</div>
                <div className="incoming-resource-value" style={{ fontSize: '1.25rem' }}>
                  {statistics.configs_with_trunk || 0}
                </div>
                <div style={{ fontSize: '0.6875rem', opacity: 0.7 }}>
                  {calculatePercentage(statistics.configs_with_trunk, statistics.total_configs)}% do total
                </div>
              </div>
            </div>

            <div className="incoming-resource-card" style={{ background: '#fee2e2', color: '#991b1b' }}>
              <FontAwesomeIcon icon={faRecordVinyl} className="incoming-resource-icon" />
              <div className="incoming-resource-text">
                <div className="incoming-resource-title">Com Gravação</div>
                <div className="incoming-resource-value" style={{ fontSize: '1.25rem' }}>
                  {statistics.configs_with_recording || 0}
                </div>
                <div style={{ fontSize: '0.6875rem', opacity: 0.7 }}>
                  {calculatePercentage(statistics.configs_with_recording, statistics.total_configs)}% do total
                </div>
              </div>
            </div>

            <div className="incoming-resource-card enabled">
              <FontAwesomeIcon icon={faVolumeUp} className="incoming-resource-icon" />
              <div className="incoming-resource-text">
                <div className="incoming-resource-title">Com Áudio</div>
                <div className="incoming-resource-value" style={{ fontSize: '1.25rem' }}>
                  {statistics.configs_with_audio || 0}
                </div>
                <div style={{ fontSize: '0.6875rem', opacity: 0.7 }}>
                  {calculatePercentage(statistics.configs_with_audio, statistics.total_configs)}% do total
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Configurações Recentes */}
        {configs.length > 0 && (
          <div className="ccv2-config-card" style={{ padding: '1.5rem' }}>
            <h3 className="incoming-section-title">Últimas Configurações</h3>
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead>
                  <tr>
                    <th>Máscara</th>
                    <th>Tipo</th>
                    <th>Status</th>
                    <th>Criado em</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.slice(0, 10).map(config => (
                    <tr key={config.id}>
                      <td>
                        <Link to={`/config/chamadas-entrada/${config.id}/visualizar`} style={{ color: '#4A7BA7', fontWeight: 600, textDecoration: 'none' }}>
                          {config.mask}
                        </Link>
                      </td>
                      <td>
                        <span className="ccv2-badge ccv2-badge--info">
                          {getDestinationTypeLabel(config.destination_type_main)}
                        </span>
                      </td>
                      <td>
                        {config.active ? (
                          <span className="ccv2-badge ccv2-badge--success">Ativo</span>
                        ) : (
                          <span className="ccv2-badge">Inativo</span>
                        )}
                      </td>
                      <td>{new Date(config.created_at).toLocaleDateString('pt-BR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default IncomingCallsStatistics;
